import React, { useMemo, useState } from "react";

import { Activity, ArrowLeft, ArrowRight, SquarePlay } from "lucide-react";

import SynopsisLegendInfo from "./SynopsisLegendInfo";
import SynopsisSectionCard from "./SynopsisSectionCard";
import { useOrgSyntheticDataToggle } from "../../../Hooks/useOrgSyntheticDataToggle";
import useXFactorKpiAccountBriefing from "../../../Hooks/useXFactorKpiAccountBriefing";
import useXFactorKpiScoresLatest from "../../../Hooks/useXFactorKpiScoresLatest";
import { BubbleCell } from "../../../intelligence-briefing-library/components/hawkeye/BubbleCell";
import { getCategoryLabel } from "../../../intelligence-briefing-library/constants/kpiCategories";
import type { MetricsConfiguration } from "../../../intelligence-briefing-library/hooks/hawkeyeMetricsCore";
import type { AccountMatrixData } from "../../../intelligence-briefing-library/hooks/useHawkeyeMetrics";
import { formatIsoDateMonDayYear } from "../../../Utils/Helper/Helper";
import type { KpiCategoryItem } from "../../AccountWorkspace/components/Kpis";
import { Modal } from "../../TempViewsRendering/components/Chrome";
import KpiExecutiveBrief from "../../TempViewsRendering/components/KpiExecutiveBrief";
import KpiSituationCard from "../../TempViewsRendering/components/KpiSituationCard";

/** Same label as Always On Channel Attention → Account Health tab. */
const ENTERPRISE_KPIS_LABEL = "Account Health";
const HEALTH_SNAPSHOT_SECTION_TITLE = "Account Health";
/** Same control as Business Health's header — opens the xfactor KPI Situation Card. */
const SITUATION_CARD_LABEL = "Situation Card";
/** The L2 read behind the card, as xfactor opens it from its Situation Card. */
const EXECUTIVE_BRIEF_LABEL = "Executive Brief";

/** Short labels matching synopsis mock (where different from full AO titles). */
const SHORT_CATEGORY_LABELS: Record<string, string> = {
  health: "Account Health",
  financial: "Financial Performance",
  adoption: "Adoption",
  support: "Support & Risk",
  engagement: "Product Engagement",
  experience: "Customer Experience",
};

const getDisplayCategoryLabel = (categoryId: string): string =>
  SHORT_CATEGORY_LABELS[categoryId] || getCategoryLabel(categoryId);

type HealthSnapshotSectionProps = {
  /** Hawkeye-sourced fallback — used when xfactor's kpi/scores/latest has no data yet. */
  overallScore: number | null;
  categories: KpiCategoryItem[];
  loading: boolean;
  onOpenChannel: () => void;
  accountMatrixData: AccountMatrixData | null;
  configuration: MetricsConfiguration;
  orderedCategories: string[];
  /** EB customer id, used to resolve this account's xfactor KPI briefing/scores. */
  accountId?: string;
  organizationName?: string | null;
};

const getTrendLabel = (score: number | null): string => {
  if (score == null || !Number.isFinite(score)) return "—";
  if (score >= 80) return "↑ Healthy";
  if (score >= 50) return "→ Stable";
  return "↓ Declining";
};

const HealthSnapshotSection: React.FC<HealthSnapshotSectionProps> = ({
  overallScore,
  categories,
  loading,
  onOpenChannel,
  accountMatrixData,
  configuration,
  orderedCategories,
  accountId,
  organizationName,
}) => {
  const [showSituationCard, setShowSituationCard] = useState(false);
  const [showExecutiveBrief, setShowExecutiveBrief] = useState(false);
  const {
    data: kpiSituationData,
    date: kpiSituationDate,
    loading: kpiSituationLoading,
  } = useXFactorKpiAccountBriefing(showSituationCard ? accountId : undefined);

  /**
   * Additive: the bubble-row strip itself is still Hawkeye-sourced via
   * `overallScore`/`categories` props by default — xfactor's kpi/scores/latest
   * (live 2026-09-21) is the first endpoint fast enough to auto-load here (no
   * AI briefing required), so prefer it when it has real scored data and fall
   * back to the Hawkeye-sourced props otherwise. This closes the one KPI
   * surface that couldn't be migrated before: no date picker fits this
   * auto-loading card.
   */
  const xfactorScores = useXFactorKpiScoresLatest(accountId);
  const xfactorHasScores =
    !xfactorScores.loading &&
    !xfactorScores.notFound &&
    Boolean(xfactorScores.data) &&
    (xfactorScores.data?.coverage?.scored_categories ?? 0) > 0;

  const xfactorOverallScore = xfactorHasScores
    ? (xfactorScores.data?.overall_score ?? null)
    : null;
  const xfactorCategories: KpiCategoryItem[] = xfactorHasScores
    ? (xfactorScores.data?.categories ?? [])
        .filter(c => c.score != null)
        .map(c => ({
          id: c.category_id,
          label: getDisplayCategoryLabel(c.category_id),
          score: c.score as number,
          trendLabel: getTrendLabel(c.score as number),
          trendDirection: "flat",
        }))
    : [];

  const effectiveOverallScore = xfactorHasScores
    ? xfactorOverallScore
    : overallScore;
  const effectiveCategories = xfactorHasScores ? xfactorCategories : categories;

  const previewCategories = effectiveCategories.slice(0, 6);
  const hasOverallScore =
    effectiveOverallScore != null && Number.isFinite(effectiveOverallScore);
  const trendLabel = getTrendLabel(effectiveOverallScore);
  const hasData = hasOverallScore || previewCategories.length > 0;

  const displayCategoryIds = useMemo(() => {
    if (orderedCategories.length > 0) return orderedCategories;
    return previewCategories.map(c => c.id);
  }, [orderedCategories, previewCategories]);

  /** Same tooltip payload as AO Channel Enterprise KPIs (Categories view). */
  const overallTooltipCategories = useMemo(() => {
    if (!accountMatrixData) return [];
    return displayCategoryIds.map(categoryId => {
      const categoryScore = accountMatrixData.categoryScores.find(
        cs => cs.category === categoryId
      );
      const weight = configuration.categoryWeights[categoryId];
      return {
        name: getCategoryLabel(categoryId),
        score: categoryScore?.score || 0,
        weight: typeof weight === "number" ? weight : 0,
      };
    });
  }, [accountMatrixData, configuration.categoryWeights, displayCategoryIds]);

  const getCategoryTooltipMetrics = (categoryId: string) => {
    if (!accountMatrixData) return [];
    const categoryData = accountMatrixData.categoryScores.find(
      cs => cs.category === categoryId
    );
    if (!categoryData?.metrics) return [];

    const includedMetricIds = configuration.includedMetrics[categoryId] || [];

    return categoryData.metrics
      .filter(metric => {
        if (!includedMetricIds.includes(metric.metric_id)) return false;
        const weight =
          configuration.metricWeights[categoryId]?.[metric.metric_id];
        return typeof weight === "number" && weight > 0;
      })
      .map(metric => {
        const metricWeight =
          configuration.metricWeights[categoryId]?.[metric.metric_id];
        return {
          name: metric.metric_name || metric.metric_id,
          value: metric.normalized_value || 0,
          weight: typeof metricWeight === "number" ? metricWeight : 0,
        };
      });
  };

  const OVERALL_SIZE = 48;
  const CATEGORY_SIZE = 36;
  const { useSyntheticData } = useOrgSyntheticDataToggle();

  return (
    <>
      <SynopsisSectionCard
        icon={Activity}
        title={HEALTH_SNAPSHOT_SECTION_TITLE}
        titleInfo={<SynopsisLegendInfo label={HEALTH_SNAPSHOT_SECTION_TITLE} />}
        linkLabel="Always On Intelligence"
        onLinkClick={onOpenChannel}
        headerExtra={
          <div className="flex flex-wrap items-center gap-2">
            {useSyntheticData ? (
              <span
                className="whitespace-nowrap rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium leading-none text-amber-900"
                role="status"
              >
                Synthetic Data Mode is enabled. KPIs and metrics displayed are
                based on sample data, not live production data.
              </span>
            ) : null}
            {accountId ? (
              <button
                type="button"
                onClick={() => setShowSituationCard(true)}
                aria-haspopup="dialog"
                title={SITUATION_CARD_LABEL}
                aria-label={SITUATION_CARD_LABEL}
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap border-0 bg-transparent p-0 text-xs font-medium leading-none text-[#7C3AED] transition-colors hover:underline"
              >
                <SquarePlay size={16} aria-hidden />
                <span>{SITUATION_CARD_LABEL}</span>
              </button>
            ) : null}
          </div>
        }
      >
        {loading ? (
          <p className="m-0 text-sm text-gray-400">
            Loading {ENTERPRISE_KPIS_LABEL.toLowerCase()}…
          </p>
        ) : hasData ? (
          <div className="relative flex w-full flex-wrap items-start justify-between gap-y-6">
            {/* Connector line through bubble centers (desktop). */}
            <div
              className="pointer-events-none absolute left-6 right-6 top-[24px] hidden h-px bg-gray-200 sm:block"
              aria-hidden
            />

            {/* Always show Overall; BubbleCell renders "–" when score is missing */}
            <div className="relative z-[1] flex min-w-[5.5rem] flex-col items-center gap-1.5 px-1">
              <div className="flex h-12 items-center justify-center bg-white">
                <BubbleCell
                  score={hasOverallScore ? effectiveOverallScore : null}
                  isOverall
                  size={OVERALL_SIZE}
                  showTooltip
                  tooltipTitle="Score"
                  hasDigest={
                    xfactorHasScores || Boolean(accountMatrixData?.hasDigest)
                  }
                  aiScore={
                    hasOverallScore && !xfactorHasScores
                      ? accountMatrixData?.aiOverallScore
                      : undefined
                  }
                  divergenceWarning={
                    hasOverallScore &&
                    !xfactorHasScores &&
                    accountMatrixData?.aiOverallScore !== undefined &&
                    Math.abs(
                      (effectiveOverallScore ?? 0) -
                        accountMatrixData.aiOverallScore
                    ) > 20
                  }
                  categories={
                    hasOverallScore && !xfactorHasScores
                      ? overallTooltipCategories
                      : []
                  }
                />
              </div>
              <div className="text-sm font-semibold text-gray-800">Overall</div>
              <div className="text-xs text-gray-400">{trendLabel}</div>
            </div>

            {previewCategories.map(category => {
              const aiCatScore = xfactorHasScores
                ? undefined
                : accountMatrixData?.aiCategoryScores?.[category.id];
              const label = getDisplayCategoryLabel(category.id);
              return (
                <div
                  key={category.id}
                  className="relative z-[1] flex min-w-[4.75rem] flex-1 flex-col items-center gap-1 px-1"
                >
                  <div className="flex h-10 items-center justify-center bg-white">
                    <BubbleCell
                      score={category.score}
                      size={CATEGORY_SIZE}
                      showTooltip
                      tooltipTitle={getCategoryLabel(category.id)}
                      hasDigest={
                        xfactorHasScores ||
                        Boolean(accountMatrixData?.hasDigest)
                      }
                      aiScore={aiCatScore}
                      divergenceWarning={
                        aiCatScore !== undefined &&
                        Math.abs(category.score - aiCatScore) > 20
                      }
                      metrics={
                        xfactorHasScores
                          ? []
                          : getCategoryTooltipMetrics(category.id)
                      }
                    />
                  </div>
                  <div className="text-sm font-semibold tabular-nums text-gray-800">
                    {Math.round(category.score)}
                  </div>
                  <div className="w-full text-center text-xs font-medium leading-snug text-gray-500">
                    {label}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-10 text-center">
            <p className="m-0 text-sm font-medium text-gray-700">
              No {ENTERPRISE_KPIS_LABEL.toLowerCase()} for this account
            </p>
            <p className="m-0 mt-2 text-sm text-gray-400">
              Configure KPIs in Always On Channel · {ENTERPRISE_KPIS_LABEL},
              then reopen Synopsis.
            </p>
            <button
              type="button"
              className="mt-4 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-sm font-medium leading-none text-[#7C3AED] hover:underline"
              onClick={onOpenChannel}
            >
              <ArrowRight
                size={14}
                strokeWidth={2.5}
                className="shrink-0"
                aria-hidden
              />
              <span>Open AO Intelligence · {ENTERPRISE_KPIS_LABEL}</span>
            </button>
          </div>
        )}
      </SynopsisSectionCard>

      <Modal
        open={showSituationCard}
        onClose={() => {
          setShowSituationCard(false);
          setShowExecutiveBrief(false);
        }}
        width="max-w-5xl"
        title={
          <span>
            {showExecutiveBrief ? EXECUTIVE_BRIEF_LABEL : SITUATION_CARD_LABEL}{" "}
            —{" "}
            <span style={{ color: "#ad46ff" }}>
              {organizationName || "Account"}
            </span>
          </span>
        }
        subtitle={
          formatIsoDateMonDayYear(kpiSituationDate) ||
          kpiSituationDate ||
          undefined
        }
        actions={
          <button
            type="button"
            onClick={() => setShowExecutiveBrief(v => !v)}
            disabled={
              !showExecutiveBrief && (!kpiSituationData || kpiSituationLoading)
            }
            className="inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ backgroundColor: "#ad46ff" }}
          >
            {showExecutiveBrief ? (
              <>
                <ArrowLeft size={13} aria-hidden />
                {SITUATION_CARD_LABEL}
              </>
            ) : (
              <>
                {EXECUTIVE_BRIEF_LABEL}
                <ArrowRight size={13} aria-hidden />
              </>
            )}
          </button>
        }
      >
        {accountId && showExecutiveBrief ? (
          <KpiExecutiveBrief
            data={kpiSituationData}
            loading={kpiSituationLoading}
          />
        ) : null}
        {accountId && !showExecutiveBrief ? (
          <KpiSituationCard
            accountName={organizationName || "Unknown account"}
            data={kpiSituationData}
            loading={kpiSituationLoading}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default HealthSnapshotSection;
